import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import ClientVINPage from "@/pages/ClientVINPage";
import AdminDashboard from "@/pages/AdminDashboard";
import PartnerDashboard from "@/pages/PartnerDashboard";
import FirstLoginSetup from "@/pages/FirstLoginSetup";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Protected route component with first-login check
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, needsFirstLoginSetup } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if partner needs to complete first-login setup
  if (needsFirstLoginSetup()) {
    return <Navigate to="/first-login-setup" replace />;
  }
  
  // Allow admin and demo_partner to access partner routes
  if (requiredRole && user.role !== requiredRole && 
      !(requiredRole === 'partner' && (user.role === 'admin' || user.role === 'demo_partner'))) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// First-login setup route guard (only accessible if is_first_login is true)
const FirstLoginRoute = ({ children }) => {
  const { user, loading, needsFirstLoginSetup } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Only allow access if user needs first-login setup
  if (!needsFirstLoginSetup()) {
    // Redirect to appropriate dashboard
    return <Navigate to={user.role === 'admin' ? '/admin' : '/partner'} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/check-vin" element={<ClientVINPage />} />
      <Route 
        path="/first-login-setup" 
        element={
          <FirstLoginRoute>
            <FirstLoginSetup />
          </FirstLoginRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/partner/*" 
        element={
          <ProtectedRoute requiredRole="partner">
            <PartnerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
